import { AxiosError, AxiosHeaders } from 'axios';
import { log } from '@/logger/logger';
import { WorkflowAppVisibility } from '@/types/workflow-app.types';
import type { WorkflowApp, CreateWorkflowAppRequestDto, UpdateWorkflowAppRequestDto } from '@/types/workflow-app.types';

const logger = log.getLogger('workflowApps.api');

// ─────────────────────────────────────────────────────────────────────
// TODO(workflow-apps): Remove mock implementation below once backend
// endpoints at /v1/workflow-apps are live.
// ─────────────────────────────────────────────────────────────────────

const mockStore = new Map<string, WorkflowApp>();

function makeFake404(url: string): AxiosError {
  const headers = new AxiosHeaders();
  return new AxiosError(
    'Request failed with status code 404',
    'ERR_BAD_REQUEST',
    { headers, url, method: 'get' } as never,
    null,
    {
      status: 404,
      statusText: 'Not Found',
      data: { message: 'WorkflowApp not found' },
      headers,
      config: { headers } as never,
    },
  );
}

function nowIso(): string {
  return new Date().toISOString();
}

// ─── End mock scaffolding ────────────────────────────────────────────

export const createWorkflowApp = async (req: CreateWorkflowAppRequestDto): Promise<WorkflowApp> => {
  try {
    // MOCK: replace with `axiosInstance.post<WorkflowApp>('/v1/workflow-apps', req)`
    await Promise.resolve();
    const created: WorkflowApp = {
      id: crypto.randomUUID(),
      workflowId: req.workflowId,
      draftOverrides: { inputs: [], outputs: [] },
      publishedAppVersionId: null,
      creatorId: 'mock-user',
      workspaceId: 'mock-workspace',
      visibility: WorkflowAppVisibility.Private,
      createdAt: nowIso(),
      updatedAt: nowIso(),
      deletedAt: null,
    };
    mockStore.set(created.id, created);
    return created;
  } catch (e) {
    logger.error('Error creating workflow app:', e);
    throw e;
  }
};

export const getWorkflowApp = async (workflowAppId: string): Promise<WorkflowApp> => {
  try {
    // MOCK: replace with `axiosInstance.get<WorkflowApp>(\`/v1/workflow-apps/${workflowAppId}\`)`
    await Promise.resolve();
    const existing = mockStore.get(workflowAppId);
    if (!existing) throw makeFake404(`/v1/workflow-apps/${workflowAppId}`);
    return existing;
  } catch (e) {
    logger.error('Error fetching workflow app:', e);
    throw e;
  }
};

export const getWorkflowAppByWorkflowId = async (workflowId: string): Promise<WorkflowApp | null> => {
  try {
    // MOCK: replace with `axiosInstance.get<WorkflowApp | null>(\`/v1/workflow-apps?workflowId=${workflowId}\`)`
    await Promise.resolve();
    for (const app of mockStore.values()) {
      if (app.workflowId === workflowId) {
        return app;
      }
    }
    return null;
  } catch (e) {
    logger.error('Error fetching workflow app by workflow id:', e);
    throw e;
  }
};

export const updateWorkflowApp = async (
  workflowAppId: string,
  patch: UpdateWorkflowAppRequestDto,
): Promise<WorkflowApp> => {
  try {
    // MOCK: replace with `axiosInstance.patch<WorkflowApp>(\`/v1/workflow-apps/${workflowAppId}\`, patch)`
    await Promise.resolve();
    const existing = mockStore.get(workflowAppId);
    if (!existing) throw makeFake404(`/v1/workflow-apps/${workflowAppId}`);
    const updated: WorkflowApp = { ...existing, ...patch, updatedAt: nowIso() };
    mockStore.set(workflowAppId, updated);
    return updated;
  } catch (e) {
    logger.error('Error updating workflow app:', e);
    throw e;
  }
};
